import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Download,
  FolderInput,
  Trash2,
  SquareX,
  LayoutGrid,
  ArrowDownUp,
  List,
  SquareCheck,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '../../../components/ui/toggle-group';
import { cn } from '../../../lib/utils';
import type { ActionType, FileType } from '../../../types';
import type { RootState } from '../../../reducers';
import { deselectAllFiles, downloadMultipleFiles } from '../librarySlice';
import { RouteParams } from '../types';
import { useQuery } from '../../../hooks/urlHooks';
import SortComponent from './Sort';
import { LibraryViewType } from '../../../types';
import MediaActions from './mediaActions';
import MediaActions2 from './mediaActions2';
import MultiAskAI from '../files/MultiAskAI';
import { useUserTags } from '../../../hooks/useTags';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { useSelectMode } from '../context/SelectModeContext';
import { setAlertMessage } from '../../dashboard/dashboardSlice';

type SelectedFileActionKeyType = 'download' | 'move' | 'delete' | 'deselect';
type ActionKeyType =
  | LibraryViewType
  | SelectedFileActionKeyType
  | 'sort'
  | 'select';

type LibraryActionType = ActionType<ActionKeyType> & {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  id?: string;
};

export const LibraryActions = () => {
  const {
    library,
    upload,
    import: importState,
  } = useSelector((state: RootState) => state);
  const { teamId, folderId } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const { selectedFiles, files } = library;
  const showSelectedFileActions = Object.keys(selectedFiles).length > 0;
  const fileArray: FileType[] = Object.values(selectedFiles);
  const { getTag, setTag } = useUserTags();
  const { selectMode, setSelectMode } = useSelectMode();

  const viewFromQuery = useQuery().view as LibraryViewType | undefined;
  const savedView = getTag('library-view-type') as LibraryViewType | null;
  const [view, setView] = useState<LibraryViewType>(
    viewFromQuery || savedView || 'grid',
  );

  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  // Get saved experience preference from user tags
  const savedExperience = getTag('media-actions-experience') as
    | 'classic'
    | 'new'
    | null;
  const useNewExperience = savedExperience === 'new';

  React.useEffect(() => {
    if (showSelectedFileActions && !selectMode) {
      setSelectMode(true);
    }
  }, [showSelectedFileActions, selectMode, setSelectMode]);

  if (
    !teamId ||
    !folderId ||
    (files.data.length === 0 &&
      !upload.activeUploads.length &&
      importState.status !== 'pending' &&
      files.status !== 'pending')
  ) {
    return (
      <div className="flex flex-col justify-between items-stretch h-auto">
        <div>{useNewExperience ? <MediaActions2 /> : <MediaActions />}</div>
        <div className="h-14 px-4" />
      </div>
    );
  }

  const handleClick = (actionKey: ActionKeyType) => {
    switch (actionKey) {
      case 'list':
      case 'grid':
        setView(actionKey);
        setTag({ key: 'library-view-type', value: actionKey });
        break;
      case 'select': {
        if (selectMode) {
          // When exiting select mode, deselect all files and explicitly exit
          dispatch(deselectAllFiles());
          setSelectMode(false);
        } else {
          // Enter select mode
          setSelectMode(true);
        }
        break;
      }
      case 'download': {
        const fileCount = fileArray.length;
        dispatch(
          setAlertMessage({
            type: 'info',
            message: `Starting download of ${fileCount} file${
              fileCount > 1 ? 's' : ''
            }...`,
          }),
        );

        dispatch(downloadMultipleFiles(fileArray));
        break;
      }
      case 'move':
        dispatch(pushModal({ type: 'move-file-modal', files: fileArray }));
        break;
      case 'delete':
        dispatch(pushModal({ type: 'delete-files-modal', files: fileArray }));
        break;
      case 'deselect':
        // Deselect all files and explicitly exit select mode
        dispatch(deselectAllFiles());
        setSelectMode(false);
        break;
      case 'sort':
        break;
      default:
        break;
    }
  };

  const selectedFileActions: LibraryActionType[] = [
    {
      key: 'download',
      label: 'Download',
      startIcon: <Download />,
    },
    {
      key: 'move',
      label: 'Move',
      startIcon: <FolderInput />,
    },
    {
      key: 'delete',
      label: 'Delete',
      startIcon: <Trash2 />,
    },
    {
      key: 'deselect',
      label: 'Deselect',
      startIcon: <SquareX />,
    },
  ];

  return (
    <div
      className="flex flex-col space-y-0 items-end mb-1"
      data-select-mode={selectMode}
    >
      <div className="pb-0 w-full flex justify-end">
        {useNewExperience ? <MediaActions2 /> : <MediaActions />}
      </div>
      <div className="flex items-center px-4 py-2 w-full h-[48px]">
        {/* Left side - Action buttons */}
        <div
          className={cn(
            'transition-all duration-200 ease-in-out h-9',
            showSelectedFileActions
              ? 'opacity-0 translate-x-4 hidden'
              : 'opacity-100 translate-x-0',
          )}
        >
          <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleClick('select')}
              className={cn(
                'h-7 px-2 rounded-md text-muted-foreground',
                selectMode && 'bg-background text-foreground shadow-sm',
              )}
            >
              <SquareCheck className="h-4 w-4" strokeWidth={1.75} />
            </Button>

            <SortComponent>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 rounded-md text-muted-foreground"
              >
                <ArrowDownUp className="h-4 w-4" strokeWidth={1.75} />
              </Button>
            </SortComponent>
          </div>
        </div>

        {/* Left side - Selected file actions (when active) */}
        <div
          className={cn(
            'transition-all duration-200 ease-in-out h-9',
            showSelectedFileActions
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-4 hidden',
          )}
        >
          <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1">
            {selectedFileActions.map(({ key, startIcon }) => (
              <Button
                key={key}
                variant="ghost"
                size="sm"
                onClick={() => handleClick(key)}
                className="h-7 px-2 rounded-md text-muted-foreground"
              >
                {React.cloneElement(startIcon as React.ReactElement, {
                  className: 'h-4 w-4',
                  strokeWidth: 1.75,
                })}
              </Button>
            ))}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side - View toggle group, always on far right of page */}
        <div className="h-9">
          <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(value: string) =>
                value && handleClick(value as ActionKeyType)
              }
              className="gap-1 relative"
            >
              <ToggleGroupItem
                value="grid"
                aria-label="Grid View"
                size="sm"
                className="h-7 px-2 rounded-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm hover:text-foreground hover:bg-background/50 hover:scale-105 active:scale-95 data-[state=off]:hover:scale-105"
              >
                <LayoutGrid
                  className="h-4 w-4 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:rotate-0 data-[state=off]:rotate-0 hover:rotate-[5deg]"
                  strokeWidth={1.75}
                  data-state={view === 'grid' ? 'on' : 'off'}
                />
                <span className="sr-only">Grid</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="list"
                aria-label="List View"
                size="sm"
                className="h-7 px-2 rounded-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm hover:text-foreground hover:bg-background/50 hover:scale-105 active:scale-95 data-[state=off]:hover:scale-105"
              >
                <List
                  className="h-4 w-4 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:rotate-0 data-[state=off]:rotate-0 hover:-rotate-[5deg]"
                  strokeWidth={1.75}
                  data-state={view === 'list' ? 'on' : 'off'}
                />
                <span className="sr-only">List</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </div>

      {templateModalOpen && (
        <MultiAskAI
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
        />
      )}
    </div>
  );
};

export default LibraryActions;
